import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Minus,
  Calendar,
  Target,
  BarChart3,
  Play,
  Pause,
  Download
} from 'lucide-react';

interface TimeState {
  id: string;
  name: string;
  description: string;
  dimensions: { [key: string]: any };
}

interface Dimension {
  id: string;
  name: string;
  type: 'number' | 'text' | 'select' | 'boolean';
  options?: string[];
  unit?: string;
}

interface GoldRushConfig {
  timeStates: TimeState[];
  dimensions: Dimension[];
  instances: string[];
}

export const GoldRushTemplate: React.FC = () => {
  const [config, setConfig] = useState<GoldRushConfig>({
    timeStates: [
      { id: 't1', name: 'Initial State', description: 'Starting point', dimensions: {} },
      { id: 't2', name: 'Milestone 1', description: 'First major checkpoint', dimensions: {} },
      { id: 't3', name: 'Milestone 2', description: 'Second major checkpoint', dimensions: {} },
      { id: 't4', name: 'Final State', description: 'End point', dimensions: {} }
    ],
    dimensions: [
      { id: 'priority', name: 'Priority Level', type: 'select', options: ['Low', 'Medium', 'High', 'Critical'] },
      { id: 'complexity', name: 'Complexity', type: 'number', unit: 'points' },
      { id: 'resources', name: 'Resources Required', type: 'number', unit: 'people' },
      { id: 'status', name: 'Status', type: 'select', options: ['Not Started', 'In Progress', 'Completed', 'Blocked'] }
    ],
    instances: ['Project Alpha', 'Project Beta', 'Project Gamma']
  });

  const [selectedTimeState, setSelectedTimeState] = useState<string>('t1');
  const [selectedInstance, setSelectedInstance] = useState<string>('Project Alpha');
  const [isPlaying, setIsPlaying] = useState(false);

  // Sample data for demonstration
  const sampleData = useMemo(() => {
    const data: { [instance: string]: { [timeState: string]: { [dimension: string]: any } } } = {};
    
    config.instances.forEach(instance => {
      data[instance] = {};
      config.timeStates.forEach((timeState) => {
        data[instance][timeState.id] = {};
        config.dimensions.forEach(dimension => {
          switch (dimension.id) {
            case 'priority':
              data[instance][timeState.id][dimension.id] = dimension.options?.[Math.floor(Math.random() * dimension.options.length)];
              break;
            case 'complexity':
              data[instance][timeState.id][dimension.id] = Math.floor(Math.random() * 10) + 1;
              break;
            case 'resources':
              data[instance][timeState.id][dimension.id] = Math.floor(Math.random() * 5) + 1;
              break;
            case 'status':
              data[instance][timeState.id][dimension.id] = dimension.options?.[Math.floor(Math.random() * dimension.options.length)];
              break;
            default:
              data[instance][timeState.id][dimension.id] = 'Sample Value';
          }
        });
      });
    });
    
    return data;
  }, [config]);

  const addTimeState = () => {
    const newId = `t${config.timeStates.length + 1}`;
    setConfig(prev => ({
      ...prev,
      timeStates: [
        ...prev.timeStates,
        { id: newId, name: `Milestone ${prev.timeStates.length}`, description: 'New checkpoint', dimensions: {} }
      ]
    }));
  };

  const removeTimeState = (timeStateId: string) => {
    if (config.timeStates.length > 2) {
      setConfig(prev => ({
        ...prev,
        timeStates: prev.timeStates.filter(ts => ts.id !== timeStateId)
      }));
    }
  };

  const addDimension = () => {
    const newId = `dim_${config.dimensions.length + 1}`;
    setConfig(prev => ({
      ...prev,
      dimensions: [
        ...prev.dimensions,
        { id: newId, name: `New Dimension ${prev.dimensions.length + 1}`, type: 'text' }
      ]
    }));
  };

  const removeDimension = (dimensionId: string) => {
    setConfig(prev => ({
      ...prev,
      dimensions: prev.dimensions.filter(d => d.id !== dimensionId)
    }));
  };

  const addInstance = () => {
    const newInstance = `Instance ${config.instances.length + 1}`;
    setConfig(prev => ({
      ...prev,
      instances: [...prev.instances, newInstance]
    }));
  };

  const removeInstance = (instance: string) => {
    if (config.instances.length > 1) {
      setConfig(prev => ({
        ...prev,
        instances: prev.instances.filter(i => i !== instance)
      }));
    }
  };

  const getDimensionColor = (dimensionId: string, value: any) => {
    switch (dimensionId) {
      case 'priority':
        switch (value) {
          case 'Low': return 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]';
          case 'Medium': return 'bg-[var(--mustard-dim)] text-[var(--mustard)]';
          case 'High': return 'bg-[var(--mustard-dim)] text-[var(--mustard)]';
          case 'Critical': return 'bg-[var(--coral-dim)] text-[var(--coral)]';
          default: return 'bg-[var(--hover-tint)] text-[var(--text-pri)]';
        }
      case 'status':
        switch (value) {
          case 'Not Started': return 'bg-[var(--hover-tint)] text-[var(--text-pri)]';
          case 'In Progress': return 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
          case 'Completed': return 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]';
          case 'Blocked': return 'bg-[var(--coral-dim)] text-[var(--coral)]';
          default: return 'bg-[var(--hover-tint)] text-[var(--text-pri)]';
        }
      default:
        return 'bg-[var(--mustard-dim)] text-[var(--mustard)]';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <Link to="/elevation/lab" className="inline-flex items-center gap-2 text-[var(--mustard)] hover:text-[var(--mustard)] mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to The Lab
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="px-4 py-2 font-bold text-xl bg-[var(--mustard-dim)] text-[var(--mustard)]">
            The Lab
          </div>
          <Target className="h-10 w-10 text-[var(--mustard)]" />
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">Gold Rush Template</h1>
            <p className="text-charcoal-600">M1 Template for technology migration strategy and executive plan of record</p>
          </div>
        </div>
        <p className="text-lg text-charcoal-700">
          A strategic methodology for managing complex technology migrations from first-generation to 
          second-generation systems. The Gold Rush template ensures coherent customer experiences during 
          messy transitions by creating executive-approved plans of record that prevent organizational 
          paralysis. It addresses the fundamental challenge: how do you migrate customers across technology 
          generations while maintaining coherent experiences for different customer segments based on their 
          relationship timeline with your organization?
        </p>
      </div>

      {/* Configuration Panel */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-charcoal-900">Template Configuration</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="px-4 py-2 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] transition-colors flex items-center gap-2"
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {isPlaying ? 'Pause' : 'Play'} Animation
            </button>
            <button className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--bg-raised)] transition-colors flex items-center gap-2">
              <Download className="h-4 w-4" />
              Export
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Time States Configuration */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-charcoal-900 flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Time States ({config.timeStates.length})
              </h3>
              <button
                onClick={addTimeState}
                className="p-1 text-[var(--mustard)] hover:text-[var(--mustard)]"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2">
              {config.timeStates.map((timeState, index) => (
                <div key={timeState.id} className="flex items-center gap-2 p-2 bg-[var(--surface)]">
                  <span className="text-sm font-medium text-charcoal-700">{index + 1}.</span>
                  <input
                    type="text"
                    value={timeState.name}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      timeStates: prev.timeStates.map(ts => 
                        ts.id === timeState.id ? { ...ts, name: e.target.value } : ts
                      )
                    }))}
                    className="flex-1 text-sm border-none bg-transparent focus:outline-none"
                  />
                  {config.timeStates.length > 2 && (
                    <button
                      onClick={() => removeTimeState(timeState.id)}
                      className="p-1 text-[var(--coral)] hover:text-[var(--coral)]"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Dimensions Configuration */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-charcoal-900 flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Dimensions ({config.dimensions.length})
              </h3>
              <button
                onClick={addDimension}
                className="p-1 text-[var(--mustard)] hover:text-[var(--mustard)]"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2">
              {config.dimensions.map((dimension) => (
                <div key={dimension.id} className="flex items-center gap-2 p-2 bg-[var(--surface)]">
                  <input
                    type="text"
                    value={dimension.name}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      dimensions: prev.dimensions.map(d => 
                        d.id === dimension.id ? { ...d, name: e.target.value } : d
                      )
                    }))}
                    className="flex-1 text-sm border-none bg-transparent focus:outline-none"
                  />
                  <button
                    onClick={() => removeDimension(dimension.id)}
                    className="p-1 text-[var(--coral)] hover:text-[var(--coral)]"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Instances Configuration */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-charcoal-900 flex items-center gap-2">
                <Target className="h-5 w-5" />
                Instances ({config.instances.length})
              </h3>
              <button
                onClick={addInstance}
                className="p-1 text-[var(--mustard)] hover:text-[var(--mustard)]"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2">
              {config.instances.map((instance) => (
                <div key={instance} className="flex items-center gap-2 p-2 bg-[var(--surface)]">
                  <input
                    type="text"
                    value={instance}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      instances: prev.instances.map(i => i === instance ? e.target.value : i)
                    }))}
                    className="flex-1 text-sm border-none bg-transparent focus:outline-none"
                  />
                  {config.instances.length > 1 && (
                    <button
                      onClick={() => removeInstance(instance)}
                      className="p-1 text-[var(--coral)] hover:text-[var(--coral)]"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* State Transition Visualization */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-charcoal-900">State Transition Visualization</h2>
          <div className="flex gap-2">
            <select
              value={selectedInstance}
              onChange={(e) => setSelectedInstance(e.target.value)}
              className="px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              {config.instances.map(instance => (
                <option key={instance} value={instance}>{instance}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {config.timeStates.map((timeState, index) => (
              <div key={timeState.id} className="flex flex-col items-center">
                <button
                  onClick={() => setSelectedTimeState(timeState.id)}
                  className={`w-12 h-12 rounded-full border-4 flex items-center justify-center font-bold text-sm transition-all ${
                    selectedTimeState === timeState.id
                      ? 'border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)]'
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:border-[var(--mustard)]'
                  }`}
                >
                  {index + 1}
                </button>
                <div className="mt-2 text-center">
                  <div className="text-sm font-medium text-charcoal-900">{timeState.name}</div>
                  <div className="text-xs text-charcoal-500">{timeState.description}</div>
                </div>
                {index < config.timeStates.length - 1 && (
                  <div className="absolute top-6 left-1/2 w-full h-0.5 bg-[var(--rule-bright)] transform translate-x-6"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Dimension Values for Selected Time State */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {config.dimensions.map(dimension => {
            const value = sampleData[selectedInstance]?.[selectedTimeState]?.[dimension.id] || 'N/A';
            return (
              <div key={dimension.id} className="p-4 bg-[var(--surface)]">
                <div className="text-sm font-medium text-charcoal-700 mb-2">{dimension.name}</div>
                <div className={`px-3 py-2  text-center font-semibold ${getDimensionColor(dimension.id, value)}`}>
                  {value} {dimension.unit && `(${dimension.unit})`}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* State Transition Matrix */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
        <h2 className="text-xl font-semibold text-charcoal-900 mb-6">State Transition Matrix</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-[var(--rule)]">
                <th className="text-left p-3 font-semibold text-charcoal-900">Instance</th>
                {config.timeStates.map(timeState => (
                  <th key={timeState.id} className="text-center p-3 font-semibold text-charcoal-900">
                    {timeState.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {config.instances.map(instance => (
                <tr key={instance} className="border-b border-[var(--rule)]">
                  <td className="p-3 font-medium text-charcoal-900">{instance}</td>
                  {config.timeStates.map(timeState => (
                    <td key={timeState.id} className="p-3">
                      <div className="grid grid-cols-2 gap-2">
                        {config.dimensions.slice(0, 2).map(dimension => {
                          const value = sampleData[instance]?.[timeState.id]?.[dimension.id] || 'N/A';
                          return (
                            <div key={dimension.id} className={`px-2 py-1 rounded text-xs text-center ${getDimensionColor(dimension.id, value)}`}>
                              {value}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Real-World Example: Nova Launch Matrix */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
        <h2 className="text-xl font-semibold text-charcoal-900 mb-6">Real-World Example: Nova Private Beta Launch</h2>
        
        <div className="mb-6">
          <p className="text-charcoal-700 mb-4">
            This is based on an actual Gold Rush template used for the Nova Private Beta Launch, showing how 
            different customer segments experience different features across release stages.
          </p>
        </div>

        {/* Customer Segments - Based on Relationship Timeline */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="p-4 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
            <h3 className="font-semibold text-[var(--mustard)] mb-2">Trail Blazers</h3>
            <p className="text-sm text-[var(--mustard)] mb-3">Longest-standing customers - came first, have been with us the longest</p>
            <div className="text-xs text-[var(--mustard)]">
              <div>• Deepest relationship with organization</div>
              <div>• Highest context and knowledge of legacy system</div>
              <div>• Private Beta → Early Access → GA journey</div>
              <div>• Most complex migration path</div>
            </div>
          </div>
          
          <div className="p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
            <h3 className="font-semibold text-[var(--text-sec)] mb-2">Forty-Niners</h3>
            <p className="text-sm text-[var(--text-sec)] mb-3">Customers joining during the messy middle - tricky transition period</p>
            <div className="text-xs text-[var(--text-sec)]">
              <div>• Moderate relationship timeline</div>
              <div>• Some context of legacy system</div>
              <div>• Early Access → GA journey</div>
              <div>• Most vulnerable to confusion</div>
            </div>
          </div>
          
          <div className="p-4 bg-[var(--green-dim)] border border-[var(--green)]">
            <h3 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-2">Settlers</h3>
            <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mb-3">Newest customers - just about to show up</p>
            <div className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
              <div>• Shortest relationship timeline</div>
              <div>• No legacy system context</div>
              <div>• GA Launch only</div>
              <div>• Cleanest experience</div>
            </div>
          </div>
        </div>

        {/* Six Primary Scenarios (A-F) */}
        <div className="mb-8">
          <h4 className="font-semibold text-charcoal-900 mb-4">Six Primary Scenarios (A-F)</h4>
          <p className="text-sm text-charcoal-700 mb-4">
            The matrix defines six primary scenarios based on customer segment (Trail Blazer, Forty-Niner, Settler) 
            and customer type (Existing vs New). Each scenario requires a coherent experience strategy.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
              <div className="font-semibold text-[var(--mustard)]">Scenario A</div>
              <div className="text-xs text-[var(--mustard)]">Trail Blazer + Existing</div>
            </div>
            <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
              <div className="font-semibold text-[var(--mustard)]">Scenario B</div>
              <div className="text-xs text-[var(--mustard)]">Trail Blazer + New</div>
            </div>
            <div className="p-3 bg-[var(--slate-dim)] border border-[var(--slate)]">
              <div className="font-semibold text-[var(--text-sec)]">Scenario C</div>
              <div className="text-xs text-[var(--text-sec)]">Forty-Niner + Existing</div>
            </div>
            <div className="p-3 bg-[var(--slate-dim)] border border-[var(--slate)]">
              <div className="font-semibold text-[var(--text-sec)]">Scenario D</div>
              <div className="text-xs text-[var(--text-sec)]">Forty-Niner + New</div>
            </div>
            <div className="p-3 bg-[var(--green-dim)] border border-[var(--green)]">
              <div className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">Scenario E</div>
              <div className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">Settler + Existing</div>
            </div>
            <div className="p-3 bg-[var(--green-dim)] border border-[var(--green)]">
              <div className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">Scenario F</div>
              <div className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">Settler + New</div>
            </div>
          </div>
        </div>

        {/* Release Stages - Executive-Governed Milestones */}
        <div className="mb-8">
          <h4 className="font-semibold text-charcoal-900 mb-4">Executive-Governed Milestones</h4>
          <p className="text-sm text-charcoal-700 mb-4">
            Three milestone states governed by executive decisions, not technical readiness. Each transition 
            is a launch date that moves the organization forward regardless of individual service completion.
          </p>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-[var(--surface)] border-2 border-[var(--rule)]">
              <div className="font-semibold text-charcoal-900 mb-2">Private Beta</div>
              <div className="text-xs text-charcoal-600 mb-2">Executive Decision Point 1</div>
              <div className="text-xs text-charcoal-500">Selected customers only</div>
            </div>
            <div className="text-center p-4 bg-[var(--surface)] border-2 border-[var(--rule)]">
              <div className="font-semibold text-charcoal-900 mb-2">Early Access</div>
              <div className="text-xs text-charcoal-600 mb-2">Executive Decision Point 2</div>
              <div className="text-xs text-charcoal-500">Approved customers</div>
            </div>
            <div className="text-center p-4 bg-[var(--surface)] border-2 border-[var(--rule)]">
              <div className="font-semibold text-charcoal-900 mb-2">GA Launch</div>
              <div className="text-xs text-charcoal-600 mb-2">Executive Decision Point 3</div>
              <div className="text-xs text-charcoal-500">All customers</div>
            </div>
          </div>
        </div>

        {/* Key Dimensions from Nova Example */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-charcoal-800 mb-3">Service Dimensions Tracked (~10 Services)</h4>
            <ul className="text-sm text-charcoal-700 space-y-2">
              <li>• <strong>Access/Registration:</strong> How customers get access</li>
              <li>• <strong>Login:</strong> Authentication method</li>
              <li>• <strong>Product Access:</strong> Available features</li>
              <li>• <strong>Support:</strong> Support channel access</li>
              <li>• <strong>Billing:</strong> Billing system access</li>
              <li>• <strong>Usage:</strong> Usage tracking access</li>
              <li>• <strong>Geographic:</strong> US vs UK access</li>
              <li>• <strong>Transition Plan:</strong> Migration strategy</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-charcoal-800 mb-3">Executive Plan of Record Methodology</h4>
            <ul className="text-sm text-charcoal-700 space-y-2">
              <li>• <strong>Clarity Capture:</strong> Document all scenarios</li>
              <li>• <strong>Stakeholder Dialogue:</strong> Uncover additional clarity</li>
              <li>• <strong>Executive Approval:</strong> Plan of record documents</li>
              <li>• <strong>Organizational Momentum:</strong> Prevent "stopping the train"</li>
              <li>• <strong>High Bar for Changes:</strong> Must justify to executives</li>
              <li>• <strong>Durable Forward Movement:</strong> Sustained progress</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Usage Guidelines */}
      <div className="bg-sage-50 p-6 border border-sage-200">
        <h3 className="text-lg font-semibold text-sage-900 mb-3">Gold Rush Strategic Methodology</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-sage-800 mb-2">When to Use</h4>
            <ul className="text-sm text-sage-700 space-y-1">
              <li>• Technology generation migrations (1st → 2nd gen)</li>
              <li>• Complex multi-service coordination</li>
              <li>• Customer experience coherence during messy transitions</li>
              <li>• Executive plan of record creation</li>
              <li>• Preventing organizational paralysis</li>
              <li>• Managing disparate service dependencies</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-sage-800 mb-2">Strategic Benefits</h4>
            <ul className="text-sm text-sage-700 space-y-1">
              <li>• Creates organizational momentum</li>
              <li>• Prevents "stopping the train" scenarios</li>
              <li>• High bar for plan changes (executive approval required)</li>
              <li>• Coherent customer experiences across segments</li>
              <li>• Durable forward movement</li>
              <li>• Stakeholder alignment through dialogue</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
